import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";

import { TProductType } from "redux/reducers/models/product";

import { showError, showSuccess } from "redux/actions/ui";
import useModal from "hooks/modals/useModal";
import { useAppDispatch } from "hooks/redux";
import { errorMessage, productTypeGroups, unsavedChangesExist } from "helpers/Helpers";

import Page from "components/page/Page";
import Popup from "components/popup/Popup";
import Input from "components/form/input";
import Card from "components/card/Card";
import Portal from "elements/Portal";
import { DeleteProductType, GetProductTypes, PutProductType } from "api/rpc/2024-04/facilityAdmin/product/type";
import { TPutProductTypeParams } from "api/rpc/2022-09/facilityAdmin/product/product";
import { useTranslation } from "react-i18next";
import FormLayout from "components/form/FormLayout";
import SelectNew from "components/select/SelectNew";

const initialState: TProductType = {
  id: 0,
  client_id: 0,
  title: "",
  locked: false,
  group: "none",
};

export default function ProductTypeEdit() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [type, setType] = useState<TProductType>(initialState);
  const [typePreserved, setTypePreserved] = useState<TProductType>(initialState);

  const { state: deletePopup, updateModal: updateDeletePopup, closeModal: closeDeletePopup } = useModal();

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProductType(source.token);
    return () => source.cancel();
  }, [id]);

  async function loadProductType(token?: CancelToken) {
    const res = await GetProductTypes({ id: Number(id) }, true, token);

    console.log(res);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting product type")));
      return;
    }

    const productType = res?.data[0];

    setType({...productType, group: !productType?.group ? "none" : productType?.group});
    setTypePreserved({...productType, group: !productType?.group ? "none" : productType?.group});
  }

  async function saveProductTypeChanges() {
    const params: TPutProductTypeParams = {
      id: Number(id),
      title: type.title,
      group: type.group === "none" ? null : type.group,
    };

    const res = await PutProductType(params, true);
    console.log(res);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(res, t("Error updating product type"))));
      return;
    }

    dispatch(showSuccess(t("Successfully updated product type")));

    setType(res.data as TProductType);
    setTypePreserved(res.data as TProductType);
  }

  async function removeProductType() {
    const res = await DeleteProductType({ id: Number(id) }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(res, t("Error deleting product type"))));
      return;
    }
    dispatch(showSuccess(t("Successfully deleted product type")));
    closeDeletePopup();
    history.push("/admin/settings/types");
  }

  return (
    <Page
      title={t("Edit Product Type")}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Types"),
          url: "/admin/settings/types",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(type, typePreserved),
        onAction: () => saveProductTypeChanges(),
        onCancel: () => setType(typePreserved),
      }}
      primaryAction={{
        content: t("Delete"),
        action: () => updateDeletePopup({ isOpen: true }),
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Title")}
                value={type.title}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setType(prev => ({ ...prev, title: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
            <SelectNew
                id="groups"
                placeholder={t("Group")}
                label={t("Group")}
                value={type?.group}
                options={productTypeGroups(t)}
                onChange={id => setType(prev => ({...prev, group: id}))}
                tooltip={t("Assign a group to count this product type's sales toward the group's column in the Server Summaries report.")}
              />
            </FormLayout.Group>
          </FormLayout>

        </Card.Section>
      </Card>

      <Portal isMounted={deletePopup.isOpen}>
        <Popup
          open={deletePopup.isOpen}
          type="warning"
          title={t("Remove Product Type")}
          description={t("Are you sure you want to remove this product type?")}
          onOk={() => removeProductType()}
          onCancel={() => closeDeletePopup()}
        />
      </Portal>
    </Page>
  );
}
